import './fristen.css';
import { useState } from 'react';
import { api, errorText } from '../../api';
import { fmtDate, monthName } from '../../lib/format';
import { daysUntil, eurC, inDays } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { USTVA_LIST } from '../accounts/labels';
import { ElsterDialog } from './ElsterDialog';
import { amountLabel, FilingDialog, firstAfter } from './FilingDialog';
import type { ElsterStand, KanzleiFiling, PeriodInfo, UstvaTabResponse, YearPeriod } from './types';

// Reiter „UStVA“ beim Mandanten (FiBu und Admin): nächste Frist mit „Übermittelt eintragen“, alle Zeiträume je Jahr (jeder
// abgelaufene einzeln eintragbar, z. B. frühere Quartale eines neuen Mandanten) und alle Übermittlungen mit
// Protokollen, Berichtigung und Entfernen. Übermittelt wird vom Steuerberater; die Kanzlei lädt sein Protokoll hoch.
export function UstvaTab({ clientId }: { clientId: string }) {
  const toast = useToast();
  const res = useApi<UstvaTabResponse>(`/api/kanzlei/ustva/${clientId}`, { live: ['fristen', 'clients'], clientId });
  const [dialog, setDialog] = useState<{ period: PeriodInfo | KanzleiFiling; filing?: KanzleiFiling } | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // An ELSTER senden (fristen/elster.ts): nur wenn auf dem Server eingeschaltet
  const el = useApi<ElsterStand>(`/api/kanzlei/ustva/${clientId}/elster`, { live: ['fristen'], clientId });
  const [erstellen, setErstellen] = useState<PeriodInfo | YearPeriod | null>(null);
  const d = res.data;
  const elster = el.data?.bereit ? el.data : null;
  if (!d) return res.error ? <p className="note">{res.error}</p> : <div className="spinner" aria-label="Lädt" />;
  const next = d.next;

  const remove = async (f: KanzleiFiling) => {
    setBusy(true);
    try {
      await api.del(`/api/kanzlei/ustva/filing/${f.id}`);
      setRemoving(null);
      await res.reload();
      toast(`Entfernt: ${f.title} · der Zeitraum ist wieder offen`);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const zurueck = async (id: string, title: string) => {
    try {
      await api.del(`/api/kanzlei/ustva/vorbereitet/${id}`);
      await el.reload();
      toast(`Zurückgezogen: ${title}`);
    } catch (err) {
      toast(errorText(err));
    }
  };

  return (
    <div className="ustva-k">
      <div className="card">
        <div className="ch">
          <h3>UStVA</h3>
          <span>{USTVA_LIST[d.client.ustva]} · der Mandant sieht Frist, Zahllast und Protokoll in seiner App</span>
        </div>
        {next ? (
          <div className="task">
            <span className="av" aria-hidden="true">
              📅
            </span>
            <div className="tx">
              <b>{next.title}</b>{' '}
              <span className={`pill ${daysUntil(next.deadline, d.today) <= 7 ? 'p-bad' : 'p-neu'}`}>
                {inDays(next.deadline, d.today)}
              </span>
              <small>
                Frist {fmtDate(next.deadline)} ·{' '}
                {next.over
                  ? 'nach der Übermittlung durch den Steuerberater hier eintragen'
                  : `der Zeitraum läuft noch, eintragen geht ab dem ${fmtDate(firstAfter(next))}`}
              </small>
            </div>
            {next.over && elster && next.kind !== 'J' && (
              <button type="button" className="btn sm dark" onClick={() => setErstellen(next)}>
                Voranmeldung erstellen
              </button>
            )}
            {next.over && (
              <button type="button" className={`btn sm${elster && next.kind !== 'J' ? '' : ' dark'}`} onClick={() => setDialog({ period: next })}>
                Übermittelt eintragen
              </button>
            )}
          </div>
        ) : (
          <p className="note">
            {d.client.ustva === 'B'
              ? 'Von der Umsatzsteuer befreit (z. B. Kleinunternehmer). Keine UStVA.'
              : 'Für diesen Mandanten gibt es hier keine UStVA.'}
          </p>
        )}
        {elster?.beimMandanten.map((v) => (
          <div key={v.id} className="task el-mandant">
            <span className="av" aria-hidden="true">
              ✉️
            </span>
            <div className="tx">
              <b>{v.title}</b> <span className="pill p-warn">beim Mandanten</span>
              <small>
                {amountLabel(v.zahllastCents / 100)} · {d.client.name} prüft und sendet in der App
              </small>
            </div>
            <button type="button" className="btn sm" onClick={() => void zurueck(v.id, v.title)}>
              Zurückziehen
            </button>
          </div>
        ))}
        {d.years.length > 0 && (
          <AllPeriods
            years={d.years}
            preferred={next?.year ?? null}
            onFile={(p) => setDialog({ period: p })}
            onElster={elster ? (p) => setErstellen(p) : null}
          />
        )}
        {d.filings.length > 0 && (
          <div className="tw">
            <table className="tust">
              <thead>
                <tr>
                  <th>Erklärung</th>
                  <th>Übermittelt</th>
                  <th>Zahllast laut Protokoll</th>
                  <th>Protokolle</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.filings.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <b>{f.title}</b>
                    </td>
                    <td data-l="Übermittelt">
                      <span className="pill p-ok">{f.corrected ? 'berichtigt' : 'übermittelt'}</span>
                      <span className="sub">am {fmtDate(f.filedOn)}</span>
                    </td>
                    <td className="num" data-l="Zahllast laut Protokoll">
                      {f.amount === null ? (
                        <span className="note">steht im Protokoll</span>
                      ) : (
                        <>
                          <b>{f.amount < 0 ? `Erstattung ${eurC(-f.amount)}` : eurC(f.amount)}</b>
                          {f.amount > 0 && <span className="sub">fällig am {fmtDate(f.due)}</span>}
                        </>
                      )}
                    </td>
                    <td data-l="Protokolle">
                      {f.files.map((p) => (
                        <div key={p.id} className="ust-file">
                          <a href={`/api/kanzlei/ustva/filing/${f.id}/file/${p.id}`} target="_blank" rel="noopener noreferrer">
                            📄 {p.name}
                          </a>
                          <span className="sub">
                            {p.correction ? 'Berichtigung' : 'Erstübermittlung'} · {fmtDate(p.filedOn)}
                            {p.uploadedBy ? ` · ${p.uploadedBy}` : ''}
                          </span>
                        </div>
                      ))}
                    </td>
                    <td className="ust-act">
                      {removing === f.id ? (
                        <span className="ust-ask">
                          <span className="note">{f.title} entfernen? Der Mandant sieht sie dann nicht mehr.</span>
                          <button type="button" className="btn sm dark" disabled={busy} onClick={() => remove(f)}>
                            Ja, entfernen
                          </button>
                          <button type="button" className="btn sm" onClick={() => setRemoving(null)}>
                            Abbrechen
                          </button>
                        </span>
                      ) : (
                        <>
                          <button type="button" className="btn sm" onClick={() => setDialog({ period: f, filing: f })}>
                            Berichtigung hochladen
                          </button>
                          <button
                            type="button"
                            className="del"
                            onClick={() => setRemoving(f.id)}
                            aria-label={`${f.title} entfernen`}
                          >
                            Entfernen
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="note ust-foot">
          {elster
            ? 'Voranmeldung erstellen: Zahlen eintragen und an ELSTER senden (Steuerberater, Bilanzbuchhalter, Steuerfachwirte) oder dem Mandanten zum Senden geben. Anders übermittelt: Protokoll hochladen mit „Übermittelt eintragen“.'
            : 'Die UStVA übermittelt der Steuerberater über DATEV/ELSTER. Hochladen: sein Übertragungsprotokoll, dazu die Zahllast in Euro (Erstattung mit Minus). Danach steht die nächste Frist automatisch da.'}
        </p>
      </div>
      {erstellen && elster && (
        <ElsterDialog
          clientId={clientId}
          clientName={d.client.name}
          period={erstellen}
          stand={elster}
          onClose={(changed) => {
            setErstellen(null);
            if (changed) void Promise.all([res.reload(), el.reload()]);
          }}
        />
      )}
      {dialog && (
        <FilingDialog
          clientId={clientId}
          clientName={d.client.name}
          period={dialog.period}
          filing={dialog.filing}
          onClose={() => {
            setDialog(null);
            void res.reload();
          }}
        />
      )}
    </div>
  );
}

const periodName = (p: YearPeriod) => (p.kind === 'Q' ? `Quartal ${p.n}` : p.kind === 'M' ? monthName(p.n) : 'Jahreserklärung');

const STATE: Record<YearPeriod['state'], { cls: string; label: string }> = {
  filed: { cls: 'p-ok', label: 'übermittelt' },
  open: { cls: 'p-warn', label: 'offen' },
  running: { cls: 'p-neu', label: 'läuft noch' },
  vorher: { cls: 'p-neu', label: 'vor Mandatsbeginn' },
};

// Alle Zeiträume eines Jahres: übermittelt, offen (mit „Übermittelt eintragen“), läuft noch. Zuerst das Jahr der
// nächsten Frist.
function AllPeriods({
  years,
  preferred,
  onFile,
  onElster,
}: {
  years: UstvaTabResponse['years'];
  preferred: number | null;
  onFile(p: YearPeriod): void;
  onElster: ((p: YearPeriod) => void) | null;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  const year = years.find((y) => y.year === (picked ?? preferred)) ?? years[years.length - 1]!;
  return (
    <div className="ust-all">
      <div className="ust-h">
        <b>Alle Zeiträume</b>
        <div className="ust-years" role="group" aria-label="Jahr">
          {years.map((y) => (
            <button
              key={y.year}
              type="button"
              className={`btn sm${y.year === year.year ? ' dark' : ''}`}
              aria-pressed={y.year === year.year}
              onClick={() => setPicked(y.year)}
            >
              {y.year}
            </button>
          ))}
        </div>
      </div>
      <div className="ust-periods">
        {year.periods.map((p) => (
          <div key={`${p.kind}-${p.n}`} className={`ust-p${p.state === 'running' || p.state === 'vorher' ? ' dim' : ''}`}>
            <span className="tx">
              <b>{periodName(p)}</b>
              <small>
                {p.kind === 'Q' ? `${p.months} · ` : ''}Frist {fmtDate(p.deadline)}
              </small>
            </span>
            <span className={`pill ${STATE[p.state].cls}`}>{STATE[p.state].label}</span>
            {p.state === 'open' && onElster && p.kind !== 'J' && (
              <button type="button" className="btn sm" aria-label={`${p.short} Voranmeldung erstellen`} onClick={() => onElster(p)}>
                Erstellen
              </button>
            )}
            {(p.state === 'open' || p.state === 'vorher') && (
              <button type="button" className="btn sm" aria-label={`${p.short} übermittelt eintragen`} onClick={() => onFile(p)}>
                Übermittelt eintragen
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
